import React from "react";
import { useSeo } from "../lib/useSeo";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";

// Shared template for the per-county tax-foreclosure SEO guides
// (/cabarrus-tax-foreclosures, /gaston-tax-foreclosures, /lincoln-tax-foreclosures).
// Each page targets "[county] tax foreclosure" searches. The full statutory
// walkthrough lives once in the Mecklenburg guide (the canonical deep page);
// these county pages carry genuinely county-specific framing (county seat,
// courthouse, live-data cross-link) plus a concise upset-bid summary, and link
// to the Mecklenburg guide for the complete process. Reuses PublicPageChrome +
// useSeo + the .legal-content styling, same as MecklenburgTaxForeclosures.tsx.

export interface CountyGuide {
  name: string; // "Cabarrus County"
  shortName: string; // "Cabarrus"
  seat: string; // county seat / courthouse city, e.g. "Concord"
  slug: string; // public-data id, e.g. "cabarrus-nc" → /public/<slug>
  seoTitle: string;
  seoDescription: string;
  intro: string[]; // county-specific intro paragraphs
}

export const COUNTY_GUIDES: Record<string, CountyGuide> = {
  cabarrus: {
    name: "Cabarrus County",
    shortName: "Cabarrus",
    seat: "Concord",
    slug: "cabarrus-nc",
    seoTitle: "Cabarrus County Tax Foreclosures (Concord, NC) & Upset Bids | Parcely",
    seoDescription:
      "How tax foreclosures work in Cabarrus County, NC — from the Concord courthouse to the 10-day upset bid window. Track Cabarrus foreclosure auctions with Parcely.",
    intro: [
      "When a property owner in Cabarrus County falls far enough behind on property taxes, the county can foreclose and sell the property at a public auction to recover what it is owed. Cabarrus sits just northeast of Charlotte — Concord, Kannapolis, Harrisburg, Mount Pleasant — and the area's fast suburban growth means a tax-foreclosure deal here can attract real competition.",
      "This is a plain-English guide to how those foreclosures work in Cabarrus, what the 10-day upset bid window is, and where to find cases that are active right now. None of it is legal or financial advice — verify anything you act on against the county and court records.",
    ],
  },
  gaston: {
    name: "Gaston County",
    shortName: "Gaston",
    seat: "Gastonia",
    slug: "gaston-nc",
    seoTitle: "Gaston County Tax Foreclosures (Gastonia, NC) & Upset Bids | Parcely",
    seoDescription:
      "How tax foreclosures work in Gaston County, NC — from the Gastonia courthouse to the 10-day upset bid window. Track Gaston foreclosure auctions with Parcely.",
    intro: [
      "When property taxes go unpaid long enough in Gaston County, the county can foreclose and sell the property at a public auction. Gaston sits west of Charlotte across the Catawba River — Gastonia, Belmont, Mount Holly, Cherryville, Bessemer City — and entry prices here tend to run below Mecklenburg, which changes the math on a deal.",
      "This is a plain-English guide to how those foreclosures work in Gaston, what the 10-day upset bid window is, and where to find cases that are open right now. None of it is legal or financial advice — verify anything you act on against the county and court records.",
    ],
  },
  lincoln: {
    name: "Lincoln County",
    shortName: "Lincoln",
    seat: "Lincolnton",
    slug: "lincoln-nc",
    seoTitle: "Lincoln County Tax Foreclosures (Lincolnton, NC) & Upset Bids | Parcely",
    seoDescription:
      "How tax foreclosures work in Lincoln County, NC — from the Lincolnton courthouse to the 10-day upset bid window. Track Lincoln foreclosure auctions with Parcely.",
    intro: [
      "When a Lincoln County property owner stops paying property taxes, the county can foreclose and sell the property at a public auction. Lincoln sits northwest of Charlotte along the western side of Lake Norman — Lincolnton, Denver, and the surrounding countryside — a smaller, more rural market where fewer foreclosures often means less competition per deal.",
      "This is a plain-English guide to how those foreclosures work in Lincoln, what the 10-day upset bid window is, and where to find active cases. None of it is legal or financial advice — verify anything you act on against the county and court records.",
    ],
  },
};

const CountyTaxForeclosureGuide: React.FC<{ county: CountyGuide }> = ({ county }) => {
  useSeo({ title: county.seoTitle, description: county.seoDescription });

  return (
    <>
      <PublicPageHeader />
      <div className="legal-page" id="main" tabIndex={-1}>
        <div className="legal-content">
          <h1>Tax Foreclosures in {county.name}, NC</h1>
          <p className="legal-updated">A plain-English guide for buyers and investors</p>

          {county.intro.map((para, i) => (
            <p key={i}>{para}</p>
          ))}

          <h2>How a {county.shortName} tax foreclosure works</h2>
          <p>
            Property taxes in North Carolina become delinquent after January 6 of the
            year after they are due, and interest starts adding up that day. The tax
            bill is only a debt at that point — there is no case to track yet. To
            actually sell the property, the county (or a law firm it hires) files a
            foreclosure action in {county.shortName} County Superior Court. NC allows
            two methods: a mortgage-style civil action under N.C.G.S. §105-374, and an
            in rem proceeding under §105-375 (often handled by a firm such as Kania
            Law). Either way it ends in a public auction, usually held at the{" "}
            {county.seat} courthouse.
          </p>
          <p>
            The case number is created when the action is <em>filed</em>, not when the
            taxes first go unpaid — so a parcel can be deeply delinquent for years with
            nothing public to find. For the full statutory walkthrough (it is the same
            across every NC county), see our{" "}
            <a href="/mecklenburg-tax-foreclosures">complete NC tax-foreclosure guide</a>.
          </p>

          <h2>The 10-day upset bid window</h2>
          <p>
            This is the part that makes NC tax sales different. The highest bidder at
            the {county.seat} auction does not own the property yet. After the sale is
            reported, North Carolina law (N.C.G.S. §1-339.25) opens a{" "}
            <strong>10-day window</strong> where anyone can raise the bid by the greater
            of <strong>5% or $750</strong>, post the required deposit, and become the new
            high bidder. Each qualifying upset bid <strong>restarts the 10-day clock</strong>,
            so a property can stay open for weeks. Only when 10 full days pass with no new
            bid does the court confirm the sale and deliver the deed. The opening number
            is often nowhere near where these land.
          </p>

          <h2>Finding {county.shortName} foreclosures</h2>
          <p>
            The cases run through the county courthouse. To see what is active or place
            an upset bid, you contact the {county.name} courthouse in {county.seat} — the
            Clerk of Superior Court's office holds the sale file and takes upset bids
            (you post the deposit there during the 10-day window). The {county.shortName}{" "}
            County tax office publishes delinquent accounts heading toward sale. None of
            these sources tell you at a glance which windows are open today, which is the
            hard part.
          </p>

          <h2>How Parcely helps</h2>
          <p>
            Parcely pulls these public sources into one place for {county.shortName} and
            the rest of the Charlotte metro. You can see active cases on a map, filter to
            properties with an open upset bid window, and watch the bid history on a
            parcel instead of checking the courthouse file by hand.
          </p>
          <p>
            <a
              href={`/public/${county.slug}`}
              className="btn-primary"
              style={{ display: "inline-block", textDecoration: "none", marginTop: "0.5rem" }}
            >
              See {county.shortName} foreclosure data →
            </a>
          </p>

          <hr className="legal-divider" />
          <p>
            <strong>This is general information, not legal or financial advice.</strong>{" "}
            Tax foreclosure rules, deposit amounts, and deadlines change, and every case
            has its own facts. Confirm anything you plan to act on with the {county.name}{" "}
            tax office, the Clerk of Superior Court in {county.seat}, and your own
            attorney before you bid.
          </p>
          <p className="legal-entity">
            <strong>Parcely LLC</strong>
            <br />
            Charlotte, NC
          </p>
        </div>
      </div>
      <PublicPageFooter />
    </>
  );
};

export default CountyTaxForeclosureGuide;
